import unfetch from 'isomorphic-unfetch';
import getConfig from 'next/config';

const {
  publicRuntimeConfig: { ACCOUNTS_SERVICE_HOST },
} = getConfig();

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await unfetch(ACCOUNTS_SERVICE_HOST + url, {
    mode: 'cors',
    cache: 'default',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  return response.json();
}

export interface AccessTokenResponse {
  /** The access token to use for identification and authorization of requests to protected endpoints. */
  access_token: string;
  /** The type of access token issued. */
  token_type: string;
  /** The lifetime (in seconds) for which the access token is valid. */
  expires_in: number;
  /** The long-lived refresh token which can be exchanged for a new access token. */
  refresh_token?: string;
}

/**
 * Login via basic username and password.
 */
async function localAuth(email: string, password: string): Promise<AccessTokenResponse> {
  return request('/auth/local', {
    method: 'POST',
    body: JSON.stringify({
      username: email,
      password,
    }),
  });
}

/**
 * Login via Google SSO issued idToken.
 */
async function googleAuth(idToken: string): Promise<AccessTokenResponse> {
  return request('/auth/google', {
    method: 'POST',
    body: JSON.stringify({
      idToken,
    }),
  });
}

/**
 * Login via Facebook SSO issued accessToken.
 */
async function facebookAuth(accessToken: string): Promise<AccessTokenResponse> {
  return request('/auth/facebook', {
    method: 'POST',
    body: JSON.stringify({
      accessToken,
    }),
  });
}

/**
 * Refresh current session and obtain a new access token.
 * @param refToken Refresh token to use for refreshing credentials. Only necessary in stateless environments. (optional)
 */
async function refresh(refToken?: string): Promise<AccessTokenResponse> {
  const body = refToken == null ? undefined : { token: refToken };

  return request('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Logout of the current session.
 * @param refToken Refresh token to use for destroying session. Only necessary in stateless environments. (optional)
 */
async function logout(refToken?: string): Promise<void> {
  const body = refToken == null ? undefined : { token: refToken };

  return request('/auth/logout', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export default {
  localAuth,
  googleAuth,
  facebookAuth,
  refresh,
  logout
};
